import React from "react";
import { RootState } from "app/lib/store/store";
import { useDispatch, useSelector } from "react-redux";
import {
  nextStep,
  prevStep,
  setStep,
  addStepsData,
} from "app/lib/store/slices/steps/loanTypesSteps";
import MainTitle from "app/components/common/typography/MainTitle";
import ContinueButton from "app/components/common/buttons/Continue";
import PreviousButtom from "app/components/common/buttons/Previous";
import SubTitle from "app/components/common/typography/SubTitle";
import {
  getRentalRate,
  RateType,
  LoanFor,
  VehicleCondition,
} from "app/utils/rentalRate";
import { formatDate } from "app/utils/dateFormatter";

import { toast } from "react-hot-toast";
import PenWithBg from "app/assets/icons/PenWithBg";
import { assessmentPercentage } from "app/constants/loanCalculateFee";

const Summary: React.FC = () => {
  const dispatch = useDispatch();
  const { vehicleDetail, loanApplication, applicants, ...others } = useSelector(
    (state: RootState) => state.loanTypesSteps.stepsData
  );

  const JointapplicationData = useSelector(
    (state: RootState) => state.loanTypesSteps.jointApplication
  );

  const { applicationType, loanFor, rateType } = loanApplication || {};

  const {
    dateOfBirth,
    firstName,
    lastName,
    employment,
    liabilities,
    expenses,
  } = applicants || {};

  const { employerName, employmentType } = employment || {};

  const isjointApplication = applicationType === "joint";
  const isRefinance = vehicleDetail?.purchaseFrom === "Refinance";

  const employmentDetailsData = isjointApplication
    ? JointapplicationData &&
      JointapplicationData?.YourDetails &&
      `${JointapplicationData?.YourDetails?.employment?.employerName ?? ""}, ${
        JointapplicationData?.YourDetails?.employment?.employmentType ?? ""
      }`
    : `${employerName ?? ""}, ${employmentType ?? ""}`;

  const secemploymentDetailsData =
    JointapplicationData?.Coborrower &&
    `${JointapplicationData?.Coborrower?.employment?.employerName ?? ""}, ${
      JointapplicationData?.Coborrower?.employment?.employmentType ?? ""
    }`;

  const secPersonalDetailsData =
    JointapplicationData?.YourDetails &&
    `${JointapplicationData?.Coborrower?.firstName ?? ""} ${
      JointapplicationData?.Coborrower?.lastName ?? ""
    } , ${formatDate(JointapplicationData?.Coborrower?.dateOfBirth)}`;

  const personalDetailsData = isjointApplication
    ? JointapplicationData &&
      JointapplicationData?.YourDetails &&
      `${JointapplicationData?.YourDetails?.firstName ?? ""} ${
        JointapplicationData?.YourDetails?.lastName ?? ""
      } , ${formatDate(JointapplicationData?.YourDetails?.dateOfBirth)}`
    : `${firstName ?? ""} ${lastName ?? ""} ,${formatDate(dateOfBirth)}`;

  const Cartype = vehicleDetail?.make ?? "";
  const CarModelYear = [vehicleDetail?.model, vehicleDetail?.year]
    .filter(Boolean)
    .join(",");

  const expensesLiabilities = !isjointApplication
    ? `${liabilities?.creditCardLimits} , ${liabilities?.sumOfAllOtherLoans}`
    : `${
        JointapplicationData?.YourDetails?.liabilities?.creditCardLimits ?? 0
      } , ${
        JointapplicationData?.Coborrower?.liabilities?.creditCardLimits ?? 0
      }`;

  const Financingtenure = loanApplication?.rateType ?? "";
  const Conditionpurchase = [
    vehicleDetail?.purchaseFrom,
    vehicleDetail?.vehicleCondition,
  ]
    .filter(Boolean)
    .join(",");

  const sunnary = [
    {
      title: "Purpose of loan",
      SubTitle: `${loanFor ?? ""} loan`,
      step: 1,
    },
    {
      title: "Condition & purchase",
      SubTitle: `${Conditionpurchase}`,
      step: 2,
    },
    {
      title: "Car brand",
      SubTitle: Cartype,
      step: 3,
    },
    {
      title: "Car model & year",
      SubTitle: CarModelYear,
      step: 4,
    },
    {
      title: "Financing tenure",
      SubTitle: `${Financingtenure}`,
      step: isRefinance ? 6 : 5,
    },

    {
      title: `Personal details ${isjointApplication ? "(1&2)" : ""}`,
      SubTitle: personalDetailsData,
      step: isjointApplication ? (isRefinance ? 19 : 18) : isRefinance ? 8 : 7,
    },
    {
      title: `Employment details ${isjointApplication ? "(1&2)" : ""}`,
      SubTitle: employmentDetailsData,
      step: isjointApplication ? (isRefinance ? 21 : 20) : isRefinance ? 10 : 9,
    },

    // ...(isjointApplication
    //   ? [
    //       {
    //         title: "Employment details 2",
    //         SubTitle: secemploymentDetailsData,
    //         step: isRefinance ? 20 : 19,
    //       },
    //       {
    //         title: `Personal details 2`,
    //         SubTitle: secPersonalDetailsData,
    //         // step: isRefinance ? 20 : 19,
    //         step: 17,
    //       },
    //     ]
    //   : []),
    {
      title: `Liabilities`,
      SubTitle: expensesLiabilities,
      step: isjointApplication ? 22 : isRefinance ? 12 : 11,
    },
  ];

  const isRateType = (value: unknown): value is RateType =>
    typeof value === "string" && ["fixed", "variable"].includes(value);
  const isLoanFor = (value: unknown): value is LoanFor =>
    typeof value === "string" && ["personal", "business"].includes(value);
  const isVehicleCondition = (value: unknown): value is VehicleCondition =>
    typeof value === "string" && ["new", "demo", "used"].includes(value);

  const onSubmit = () => {
    const currentYear = new Date().getFullYear();
    const isPropertyOwner = isjointApplication
      ? JointapplicationData?.YourDetails?.mailingAddress?.housingStatus ===
        "Owner"
      : applicants?.mailingAddress?.housingStatus === "Owner";

    if (!vehicleDetail) {
      console.error("Vehicle details are missing.");
      return;
    }

    const vehicleCondition = vehicleDetail.vehicleCondition;
    const modelYears = vehicleDetail.year
      ? currentYear - vehicleDetail.year
      : 0;

    if (
      isRateType(rateType) &&
      isLoanFor(loanFor) &&
      typeof isPropertyOwner === "boolean" &&
      isVehicleCondition(vehicleCondition) &&
      modelYears >= 0
    ) {
      const rentalRate = getRentalRate({
        rateType,
        loanFor,
        isPropertyOwner,
        vehicleCondition,
        modelYears:
          vehicleCondition === "new" || vehicleCondition === "demo"
            ? 0
            : modelYears,
      });

      if (!rentalRate || rentalRate === 0) {
        toast.error(
          "Unable to calculate rental rate. Please review vehicle or rate type information."
        );
        return;
      } else {
        const variableRentalRate =
          Number(rentalRate) + Number(assessmentPercentage);

        dispatch(
          addStepsData({
            ...others,
            loanApplication: {
              ...loanApplication,
              interestRate: Number(rentalRate),
              ...(rateType === "variable" && {
                assessmentRate: variableRentalRate,
              }),
            },
          })
        );
      }
      dispatch(nextStep());
    }
  };

  const handlePrevious = () => {
    if (isjointApplication) {
      dispatch(setStep(22));
    } else {
      dispatch(prevStep());
    }
  };

  return (
    <>
      <div className="w-full  sm:w-[80%] xl:w-[55%]  mx-auto text-center px-4 sm:px-0">
        <MainTitle title="Summary" />
        <SubTitle title="Please review all the information carefully before proceeding. By confirming, you acknowledge that the details provided are accurate and complete" />

        <div className="flex flex-wrap gap-4 sm:gap-2 md:gap-2  lg:gap-3 justify-between">
          {sunnary.map((item) => {
            return (
              <div
                key={item?.title}
                className="w-full sm:w-[49%] p-4 flex justify-between items-center border borer-[#EAECF0] mb-2 rounded-[5px] cursor-pointer"
                onClick={() => {
                  dispatch(setStep(item?.step));
                }}
              >
                <div className="text-left">
                  <h5 className="text-[#313131] font-Mulish text-base font-bold">
                    {item?.title}
                  </h5>
                  <h6 className="text-[#585C7B] text-[14px] font-Mulish capitalize">
                    {item?.SubTitle}
                  </h6>
                </div>
                <div className="">
                  <PenWithBg />
                </div>
              </div>
            );
          })}
        </div>
        <div className="flex justify-between my-10 mt-12">
          <PreviousButtom
            className="lg:!w-[20%] md:!w-[23%]"
            onClick={handlePrevious}
          >
            Previous
          </PreviousButtom>
          <ContinueButton
            className="lg:!w-[30%]  md:!w-[33%] px-4"
            type="button"
            onClick={onSubmit}
          >
            <span className="block sm:hidden">Next</span>
            <span className="hidden sm:block">Confirm & Continue</span>
          </ContinueButton>
        </div>
      </div>
    </>
  );
};

export default Summary;
